import React, { useEffect, useReducer, useState } from 'react';

import { useClient } from '../../contexts';
import { usePermissions } from '../../contexts/Permissions/PermissionsCheck';

import { AudienceNetwork } from './types';
import { canTargetAudiences } from './utils';

type AudiencesContextType = {
  networks: AudienceNetwork[];
  /* Entrée « Groupes » du menu et sa page */
  visible: boolean;
  /* Relance GET /me/audiences : effectifs à jour après un ajout ou un retrait */
  reload: () => void;
};

export const AudiencesContext = React.createContext<AudiencesContextType>({
  networks: [],
  visible: false,
  reload: () => {},
});

/**
 * Groupes issus de l'analyse que l'utilisateur peut cibler : un seul GET /me/audiences,
 * partagé par le menu, la route et la page « Groupes ».
 */
export const AudiencesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { get } = useClient();
  const { hasPermission } = usePermissions();
  const canView = hasPermission({ page: 'audiences', permission: 'view' });
  const canPublish = hasPermission({ page: 'publish', permission: 'create' });
  const [networks, setNetworks] = useState<AudienceNetwork[]>([]);
  const [version, reload] = useReducer(x => x + 1, 0);

  useEffect(() => {
    if (!canView || !canPublish) return;
    return get('/me/audiences', (response: { data: AudienceNetwork[] }) =>
      setNetworks(response.data ?? []),
    );
  }, [canView, canPublish, get, version]);

  return (
    <AudiencesContext.Provider
      value={{
        networks,
        visible: canTargetAudiences(canView, canPublish, networks),
        reload,
      }}
    >
      {children}
    </AudiencesContext.Provider>
  );
};

export const useAudiences = () => React.useContext(AudiencesContext);
